/**
 * Admin-only user management, scoped to the caller's current tenant.
 *
 * "Current tenant" is the principal's home tenant for a normal admin, and
 * whichever tenant a superadmin has switched into via the tenant switcher
 * (see TenantAdmin.tsx) - the API resolves that the same way for every
 * tenant-scoped endpoint, so this page needs no special casing for either.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useIdentity } from '../hooks/useSession'
import type { PlannerUserOut } from '../lib/types'
import { shortDate } from '../lib/format'
import { Callout, ErrorCallout } from '../components/Callout'
import { Chip } from '../components/Chips'
import { InfoTip } from '../components/InfoTip'
import { Skeleton } from '../components/Metric'

const ROLE_OPTIONS: { value: string; label: string; description: string }[] = [
  { value: 'viewer', label: 'Viewer', description: 'View only - cannot edit or run anything.' },
  {
    value: 'campaign_planner',
    label: 'Planner',
    description: 'Create briefs, run recommendations, edit plans, submit for approval.',
  },
  {
    value: 'operations',
    label: 'Operations',
    description: 'View plans and inspect data health, no editing.',
  },
  {
    value: 'analyst',
    label: 'Analyst',
    description: 'Inspect scores, coverage and the audit log; can run recommendations.',
  },
  {
    value: 'approver',
    label: 'Approver',
    description: 'Approve or reject a submitted plan and create the campaign. Cannot edit a plan.',
  },
  {
    value: 'administrator',
    label: 'Admin',
    description: 'Everything in this tenant: scoring versions, data ingest, and adding users.',
  },
]

const SUPERADMIN_OPTION = {
  value: 'superadmin',
  label: 'Superadmin',
  description: 'Full access to every tenant. Only another superadmin can grant this.',
}

function UserRow({ user, canGrantSuperadmin }: { user: PlannerUserOut; canGrantSuperadmin: boolean }) {
  const queryClient = useQueryClient()
  const isSuperadminRow = user.role === 'superadmin'
  // A superadmin row must render "Superadmin" even when the viewer cannot
  // grant that role - the control is disabled below either way.
  const options =
    canGrantSuperadmin || isSuperadminRow ? [...ROLE_OPTIONS, SUPERADMIN_OPTION] : ROLE_OPTIONS

  const update = useMutation({
    mutationFn: (body: { role?: string; is_active?: boolean }) => api.updateUser(user.id, body),
    onSuccess: (result) =>
      queryClient.setQueryData<PlannerUserOut[]>(['planner-users'], (rows) =>
        (rows ?? []).map((r) => (r.id === result.id ? result : r)),
      ),
  })

  // A superadmin's own row can only be touched by another superadmin - the
  // API refuses it either way, disabling the controls just avoids a 403.
  const locked = (isSuperadminRow && !canGrantSuperadmin) || update.isPending

  return (
    <tr>
      <td className="small">
        {user.email}
        {user.display_name ? <div className="tiny faint">{user.display_name}</div> : null}
      </td>
      <td>
        <select
          value={user.role}
          disabled={locked}
          onChange={(e) => update.mutate({ role: e.target.value })}
        >
          {options.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <label className="row tight nowrap" style={{ gap: 6, cursor: locked ? 'default' : 'pointer' }}>
          <input
            type="checkbox"
            checked={user.is_active}
            disabled={locked}
            onChange={(e) => update.mutate({ is_active: e.target.checked })}
          />
          {user.is_active ? <Chip tone="ok">Active</Chip> : <Chip tone="neutral">Inactive</Chip>}
        </label>
      </td>
      <td className="tiny faint">{shortDate(user.created_at)}</td>
    </tr>
  )
}

function AddUserForm({ canGrantSuperadmin }: { canGrantSuperadmin: boolean }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState('campaign_planner')
  const options = canGrantSuperadmin ? [...ROLE_OPTIONS, SUPERADMIN_OPTION] : ROLE_OPTIONS

  const create = useMutation({
    mutationFn: () =>
      api.createUser({
        email: email.trim().toLowerCase(),
        display_name: displayName.trim() || null,
        role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planner-users'] })
      setEmail('')
      setDisplayName('')
      setRole('campaign_planner')
    },
  })

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="grow">Add a user</h2>
      </div>
      <div className="card-body stack">
        {create.isError ? <ErrorCallout error={create.error} /> : null}
        <div className="row" style={{ gap: 12 }}>
          <label className="field grow">
            Email
            <input
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field grow">
            Display name
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label className="field grow">
            <span className="row tight nowrap" style={{ gap: 4 }}>
              Role
              <InfoTip text={options.find((r) => r.value === role)?.description} />
            </span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {options.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <button
            className="primary"
            disabled={!email.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function UserAdmin() {
  const { me, loading: identityLoading } = useIdentity()
  const users = useQuery({
    queryKey: ['planner-users'],
    queryFn: api.users,
    enabled: !!me?.permissions.includes('user:manage'),
  })

  if (identityLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={4} />
        </div>
      </main>
    )
  }

  if (!me?.permissions.includes('user:manage')) {
    return (
      <main className="main">
        <div className="page">
          <Callout tone="danger" title="Admins only">
            Your role ({me?.role.replace(/_/g, ' ') ?? 'unknown'}) does not permit managing users.
          </Callout>
        </div>
      </main>
    )
  }

  const canGrantSuperadmin = me.role === 'superadmin'

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Users</h1>
            <div className="page-sub">
              Planner accounts and roles for tenant <span className="mono">{me.tenant_id}</span>.
            </div>
          </div>
        </div>

        <Callout tone="info" title="Roles are scoped to this tenant">
          A user added here only has access within tenant <span className="mono">{me.tenant_id}</span>.
          Granting the superadmin role is restricted to superadmins, since that role is not scoped
          to any one tenant.
        </Callout>

        <AddUserForm canGrantSuperadmin={canGrantSuperadmin} />

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Existing users</h2>
          </div>
          {users.isLoading ? (
            <div className="card-body">
              <Skeleton rows={4} />
            </div>
          ) : users.isError ? (
            <div className="card-body">
              <ErrorCallout error={users.error} onRetry={() => void users.refetch()} />
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Active</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {(users.data ?? []).map((user) => (
                    <UserRow key={user.id} user={user} canGrantSuperadmin={canGrantSuperadmin} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
